import React from "react";
import type { SpecType } from "../../service/SpecsRevisionService";
import SpecsValidationDialog, {
  type SpecsEditorValidationResult,
  useSpecsValidationDialogApi,
} from "./SpecsValidationDialog";
import type { SpecsCatalog } from "./specsModel/catalog";
import {
  buildSpecsEntityLabel,
  getSpecsEntityId,
  type SpecsEntityModel,
} from "./specsModel/specsLib";
import { SpecsPageView } from "./types";
import { type CreateSpecDraftInput, useSpecState } from "./useSpecState";

type NonProductSpecType = Exclude<SpecType, "product">;

type SpecsPageOption = {
  id: string;
  label: string;
};

export type SpecsCreateDraftField = {
  name: keyof CreateSpecDraftInput;
  label: string;
  helperText?: string;
};

type SpecsPageContextValue = {
  specType: NonProductSpecType;
  title: string;
  description: string;
  emptyLabel: string;
  emptyMessage: string;
  placeholder: string;
  loadingMessage: string;
  createDraftFields: SpecsCreateDraftField[];
  options: SpecsPageOption[];
  selectedValue: string;
  loading: boolean;
  error: string | null;
  catalog: SpecsCatalog | null;
  currentView: SpecsPageView;
  draftJson: string;
  draftModel: SpecsEntityModel | null;
  draftModelRevision: number;
  saveLoading: boolean;
  publishLoading: boolean;
  loadingRevisionId: string | null;
  validationOpen: boolean;
  validationResult: SpecsEditorValidationResult | null;
  onSelect: (value: string) => void;
  onReload: () => void;
  onToggleView: () => void;
  onSave: () => void;
  onPublish: () => void;
  onCreateDraft: (input: CreateSpecDraftInput) => void;
  onChangeDraft: (value: string) => void;
  onReplaceDraftFromSnapshot: (snapshot: Record<string, unknown>) => void;
  onLoadRevision: (revisionId: string) => Promise<void>;
  onBackFromHistory: (specName?: string) => void;
  onOpenValidationDialog: () => void;
  onCloseValidationDialog: () => void;
};

type SpecsPageConfig = {
  title: string;
  description: string;
  emptyLabel: string;
  emptyMessage: string;
  placeholder: string;
  loadingMessage: string;
  collectionKey: "families" | "fabrics" | "decorations";
  parseErrorMessage: string;
  createDraftFields: SpecsCreateDraftField[];
};

const SpecsPageContext = React.createContext<SpecsPageContextValue | null>(
  null
);

function getCatalogCollection(
  catalog: SpecsCatalog | null,
  collectionKey: SpecsPageConfig["collectionKey"]
) {
  if (!catalog) {
    return [];
  }

  return catalog[collectionKey] || [];
}

function getSpecsPageConfig(specType: NonProductSpecType): SpecsPageConfig {
  if (specType === "family") {
    return {
      title: "Families",
      description: "",
      emptyLabel: "No families available",
      emptyMessage: "No families are currently available.",
      placeholder: '{\n  "name": "exampleFamily"\n}',
      loadingMessage: "Loading family specs catalog...",
      collectionKey: "families",
      parseErrorMessage: "JSON must describe one family object",
      createDraftFields: [{ name: "specName", label: "Name" }],
    };
  }

  if (specType === "fabric") {
    return {
      title: "Fabrics",
      description: "",
      emptyLabel: "No fabrics available",
      emptyMessage: "No fabrics are currently available.",
      placeholder: '{\n  "name": "exampleFabric"\n}',
      loadingMessage: "Loading fabric specs catalog...",
      collectionKey: "fabrics",
      parseErrorMessage: "JSON must describe one fabric object",
      createDraftFields: [{ name: "specName", label: "Name" }],
    };
  }

  return {
    title: "Decorations",
    description: "",
    emptyLabel: "No decorations available",
    emptyMessage: "No decorations are currently available.",
    placeholder: '{\n  "id": "exampleDecoration"\n}',
    loadingMessage: "Loading decoration specs catalog...",
    collectionKey: "decorations",
    parseErrorMessage: "JSON must describe one decoration object",
    createDraftFields: [
      { name: "specName", label: "ID" },
      { name: "displayName", label: "Display name", helperText: "Optional" },
    ],
  };
}

export function SpecsPageContextProvider({
  children,
  specType,
}: React.PropsWithChildren<{ specType: NonProductSpecType }>) {
  const config = React.useMemo(() => getSpecsPageConfig(specType), [specType]);
  const modals = useSpecsValidationDialogApi();

  const getOptions = React.useCallback(
    (catalog: SpecsCatalog | null) =>
      getCatalogCollection(catalog, config.collectionKey).map((entity) => {
        const record = entity as Record<string, unknown>;
        const id = getSpecsEntityId(specType, record);
        return {
          id,
          label: buildSpecsEntityLabel(specType, record),
          entity: record,
        };
      }),
    [config.collectionKey, specType]
  );

  const parseDraft = React.useCallback(
    (draftJson: string) => {
      const parsed = JSON.parse(draftJson || "");
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error(config.parseErrorMessage);
      }

      const entity = parsed as Record<string, unknown>;
      return {
        specName: getSpecsEntityId(specType, entity),
        entity,
      };
    },
    [config.parseErrorMessage, specType]
  );

  const {
    loading,
    error,
    catalog,
    draftJson,
    draftModel,
    draftModelRevision,
    setDraftJson,
    saveLoading,
    publishLoading,
    loadingRevisionId,
    options,
    selectedSpecName,
    currentView,
    updateSearchParams,
    reloadSelectedSpec,
    createDraft,
    replaceDraftFromSnapshot,
    saveDraft,
    saveAndPublish,
    loadRevision,
  } = useSpecState<Record<string, unknown>>({
    specType,
    getOptions,
    parseDraft,
    modals,
  });

  const onSelect = React.useCallback(
    (value: string) => {
      updateSearchParams({ spec: value });
    },
    [updateSearchParams]
  );

  const onReload = React.useCallback(() => {
    if (currentView === SpecsPageView.History) {
      updateSearchParams({ view: SpecsPageView.Editor });
      return;
    }

    reloadSelectedSpec();
  }, [currentView, reloadSelectedSpec, updateSearchParams]);

  const onToggleView = React.useCallback(() => {
    const view =
      currentView === SpecsPageView.History
        ? SpecsPageView.Editor
        : SpecsPageView.History;
    updateSearchParams({
      view,
    });
  }, [currentView, updateSearchParams]);

  const onSave = React.useCallback(() => {
    void saveDraft();
  }, [saveDraft]);

  const onPublish = React.useCallback(() => {
    void saveAndPublish();
  }, [saveAndPublish]);

  const onCreateDraft = React.useCallback(
    (input: CreateSpecDraftInput) => {
      createDraft(input);
    },
    [createDraft]
  );

  const onLoadRevision = React.useCallback(
    async (revisionId: string) => {
      await loadRevision(revisionId);
    },
    [loadRevision]
  );

  const onBackFromHistory = React.useCallback(
    (specName?: string) => {
      updateSearchParams({
        spec: specName || selectedSpecName,
        view: SpecsPageView.Editor,
      });
    },
    [selectedSpecName, updateSearchParams]
  );

  const contextValue = React.useMemo(
    () => ({
      specType,
      ...config,
      options,
      selectedValue: selectedSpecName,
      loading,
      error,
      catalog,
      currentView,
      draftJson,
      draftModel,
      draftModelRevision,
      saveLoading,
      publishLoading,
      loadingRevisionId,
      validationOpen: modals.validationOpen,
      validationResult: modals.validationResult,
      onSelect,
      onReload,
      onToggleView,
      onSave,
      onPublish,
      onCreateDraft,
      onChangeDraft: setDraftJson,
      onReplaceDraftFromSnapshot: replaceDraftFromSnapshot,
      onLoadRevision,
      onBackFromHistory,
      onOpenValidationDialog: modals.openValidationDialog,
      onCloseValidationDialog: modals.closeValidationDialog,
    }),
    [
      config,
      catalog,
      currentView,
      draftJson,
      draftModel,
      draftModelRevision,
      error,
      loading,
      modals.closeValidationDialog,
      modals.openValidationDialog,
      modals.validationOpen,
      modals.validationResult,
      onBackFromHistory,
      onCreateDraft,
      onPublish,
      onReload,
      onSave,
      onSelect,
      onToggleView,
      options,
      replaceDraftFromSnapshot,
      onLoadRevision,
      publishLoading,
      loadingRevisionId,
      saveLoading,
      selectedSpecName,
      setDraftJson,
      specType,
    ]
  );

  return (
    <SpecsPageContext.Provider value={contextValue}>
      {children}
      <SpecsValidationDialog
        open={modals.validationOpen}
        validationResult={modals.validationResult}
        onClose={modals.closeValidationDialog}
      />
    </SpecsPageContext.Provider>
  );
}

export function useSpecsPageContext() {
  const context = React.useContext(SpecsPageContext);
  if (!context) {
    throw new Error(
      "useSpecsPageContext must be used within a SpecsPageContextProvider"
    );
  }

  return context;
}
